import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { Order, OrderFilters, OrderStatus, ApiResponse, PaginatedResponse } from '@/types'
import { RootState } from '@/app/store'
import { getAuthToken } from '@/utils/secureStorage'

// Base API configuration with error logging
const baseQueryWithErrorLogging = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_API_URL || '/api',
  prepareHeaders: (headers, { getState }) => {
    // Try to get token from Redux state first
    let token = (getState() as RootState).auth.token
    
    // If not in Redux state (e.g., after page refresh before rehydration), get from secure storage
    if (!token) {
      token = getAuthToken()
    }
    
    const language = (getState() as RootState).settings.settings.language
    
    if (token) {
      headers.set('authorization', `Bearer ${token}`)
    }
    
    headers.set('lang', language)
    headers.set('Content-Type', 'application/json')
    headers.set('Accept', 'application/json')
    
    return headers
  },
})

// Wrap baseQuery to add error logging
const baseQuery = async (args: any, api: any, extraOptions: any) => {
  const result = await baseQueryWithErrorLogging(args, api, extraOptions)
  
  // Log 500 errors with detailed information
  if (result.error && 'status' in result.error && result.error.status === 500) {
    const baseUrl = import.meta.env.VITE_API_URL || '/api'
    const url = typeof args === 'string' ? args : args.url
    const method = typeof args === 'string' ? 'GET' : (args.method || 'GET')
    const fullUrl = `${baseUrl}${url}`
    const errorData = result.error.data as any
  }
  
  return result
}

// Orders API slice
export const ordersApi = createApi({
  reducerPath: 'ordersApi',
  baseQuery,
  tagTypes: ['Orders'],
  endpoints: (builder) => ({
    // Submit order with comprehensive payload
    submitOrder: builder.mutation<ApiResponse<{
      order_id: string | number
      order_number: string
      total_amount: number
      remaining_amount: number
      payment_status: 'pending' | 'partial' | 'paid'
      estimated_time?: string
    }>, {
      items: Array<{
        product_id: string | number
        quantity: number
        size_id?: string | number
        additionals?: Array<{
          id: string | number
          quantity: number
        }>
        notes?: string
      }>
      customer_id?: string | number
      order_type: 'dine_in' | 'takeaway' | 'delivery' | 'car_pickup'
      table_id?: string | number
      payment_method: 'cash' | 'card' | 'online' | 'partial'
      discount_code?: string
      notes?: string
      delivery_address?: string
      estimated_delivery_time?: string
    }>({
      query: (orderData) => ({
        url: '/orders/submit',
        method: 'POST',
        body: orderData,
      }),
      invalidatesTags: ['Orders'],
    }),
    
    // Get orders with enhanced filtering
    getOrders: builder.query<ApiResponse<Order[]>, {
      status?: OrderStatus | 'all'
      order_type?: 'dine_in' | 'takeaway' | 'delivery' | 'car_pickup' | 'all'
      date_from?: string
      date_to?: string
      customer_id?: string | number
      table_id?: string | number
      limit?: number
    }>({
      query: (filters) => ({
        url: '/orders',
        params: Object.fromEntries(
          Object.entries(filters).filter(([_, value]) => value !== undefined && value !== 'all')
        ),
      }),
      providesTags: ['Orders'],
    }),
    
    // Get order details
    getOrderDetails: builder.query<ApiResponse<Order>, string | number>({
      query: (orderId) => `/orders/${orderId}`,
      providesTags: (_result, _error, id) => [{ type: 'Orders', id }],
    }),
    
    // Create new order
    createOrder: builder.mutation<ApiResponse<Order>, Partial<Order>>({
      query: (orderData) => ({
        url: '/orders',
        method: 'POST',
        body: orderData,
      }),
      invalidatesTags: ['Orders'],
    }),
    
    // Update order status
    updateOrderStatus: builder.mutation<ApiResponse<Order>, { orderId: string | number; status: OrderStatus }>({
      query: ({ orderId, status }) => ({
        url: `/orders/${orderId}/status`,
        method: 'PUT',
        body: { status },
      }),
      invalidatesTags: ['Orders'],
    }),
    
    // Cancel order
    cancelOrder: builder.mutation<ApiResponse<Order>, string | number>({
      query: (orderId) => ({
        url: `/orders/${orderId}/cancel`,
        method: 'PUT',
      }),
      invalidatesTags: ['Orders'],
    }),
    
    // Get orders with pagination
    getOrdersPaginated: builder.query<PaginatedResponse<Order>, { page?: number; per_page?: number } & OrderFilters>({
      query: ({ page = 1, per_page = 20, ...filters }) => ({
        url: '/orders/paginated',
        params: { page, per_page, ...filters },
      }),
      providesTags: ['Orders'],
    }),
    
    // Get today's orders
    getTodaysOrders: builder.query<ApiResponse<Order[]>, void>({
      query: () => '/orders/today',
      providesTags: ['Orders'],
    }),
    
    // Get orders by status
    getOrdersByStatus: builder.query<ApiResponse<Order[]>, OrderStatus>({
      query: (status) => ({
        url: '/orders',
        params: { status },
      }),
      providesTags: ['Orders'],
    }),
  }),
})

export const {
  useSubmitOrderMutation,
  useGetOrdersQuery,
  useGetOrderDetailsQuery,
  useCreateOrderMutation,
  useUpdateOrderStatusMutation,
  useCancelOrderMutation,
  useGetOrdersPaginatedQuery,
  useGetTodaysOrdersQuery,
  useGetOrdersByStatusQuery,
} = ordersApi
